import { getFromDataPath, reduceDataPath, removeByPath } from "../../../utility";
import { ArsTableCellType, ArsTableState, ArstActionContextmenuAction } from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import { arst_checkRowHeaders } from "./arst_checkRowHeaders";
import { containsAnalysisMethod } from "./containsAnalysisMethod";

/**
 * Removes an operation from its parent group.
 *
 * @param state - Current ARS table state
 * @param action - Context-menu action describing the target operation cell
 * @returns Updated ARS table state
 */
export const arst_removeOperation = <T>(
  state: ArsTableState<T>,
  action: ArstActionContextmenuAction<T>,
): ArsTableState<T> => {
  if (
    action.cellValue.cellType !== ArsTableCellType.ColumnOperation &&
    action.cellValue.cellType !== ArsTableCellType.RowOperation
  ) {
    return state;
  }

  // Get target dataPath
  const targetDataPath = action.cellValue.dataPath;

  // Check if the target dataPath exists in the bodyData
  const targetValue = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
    isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
  );
  if (!targetValue) {
    return state;
  }

  const isRemoveColumn = action.cellValue.cellType === ArsTableCellType.ColumnOperation;

  // Get sibling count
  const parentDataPath = reduceDataPath(action.cellValue.dataPath, 2);
  const parentGroup = getFromDataPath(parentDataPath, state.bodyData, (obj) =>
    isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
  );

  const siblingCount =
    parentGroup?.subGroups?.length ??
    (isRemoveColumn ? state.bodyData.columns.length : state.bodyData.rows.length);
  const newBodyData = removeByPath(state.bodyData, targetDataPath, siblingCount - 1);

  // Check if row headers need to be updated
  newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);

  // Check some operations are still present in the table, if not, clear the analysisMethodId
  if (!containsAnalysisMethod(newBodyData.columns) && !containsAnalysisMethod(newBodyData.rows)) {
    newBodyData.analysisMethodId = "";
  }

  return {
    ...state,
    bodyData: newBodyData,
    changed: true,
  };
};
